import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const AuthContext = createContext(null);
const CabangContext = createContext(null);
const IDLE_TIMEOUT_MS = 5 * 60 * 1000; // 5 menit

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cabangList, setCabangList] = useState([]);
  const [selectedCabang, setSelectedCabang] = useState("all");
  const idleTimer = useRef(null);

  const checkAuth = useCallback(async () => {
    try { const { data } = await api.get("/auth/me"); setUser(data); }
    catch (e) { setUser(null); }
    finally { setLoading(false); }
  }, []);

  const loadCabang = useCallback(async () => {
    try { const { data } = await api.get("/cabang"); setCabangList(data); } catch { /* ignore */ }
  }, []);

  useEffect(() => { checkAuth(); }, [checkAuth]);
  useEffect(() => {
    if (user) {
      loadCabang();
      if (user.role === "cabang" && user.cabang_id) setSelectedCabang(user.cabang_id);
      else setSelectedCabang("all");
    }
  }, [user, loadCabang]);

  const logout = useCallback(async (reason) => {
    try { await api.post("/auth/logout"); } catch { /* ignore */ }
    localStorage.removeItem("session_token");
    setUser(null);
    if (reason === "idle") toast.info("Sesi berakhir karena tidak aktif 5 menit");
    window.location.href = "/login";
  }, []);

  // Auto-logout after 5 minutes of inactivity
  useEffect(() => {
    if (!user) return;
    const reset = () => {
      if (idleTimer.current) clearTimeout(idleTimer.current);
      idleTimer.current = setTimeout(() => logout("idle"), IDLE_TIMEOUT_MS);
    };
    const events = ["mousemove", "mousedown", "keydown", "touchstart", "scroll"];
    events.forEach((e) => window.addEventListener(e, reset, { passive: true }));
    reset();
    return () => {
      if (idleTimer.current) clearTimeout(idleTimer.current);
      events.forEach((e) => window.removeEventListener(e, reset));
    };
  }, [user, logout]);

  return (
    <AuthContext.Provider value={{ user, setUser, loading, logout, checkAuth }}>
      <CabangContext.Provider value={{ cabangList, loadCabang, selectedCabang, setSelectedCabang }}>
        {children}
      </CabangContext.Provider>
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
export const useCabang = () => useContext(CabangContext);
